import React from "react";
import {
  Document,
  Page,
  View,
  Text,
  Link,
  StyleSheet,
} from "@react-pdf/renderer";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PdfReportData {
  vendor: string;
  category: string;
  evaluationDate: string;
  version: string;
  executiveSummary: {
    keyTakeaway: string;
    sentiment: "positive" | "neutral" | "negative";
    paragraphs: string[];
  };
  criteria: Array<{
    criterionId: string;
    title: string;
    subtitle: string;
    score: "yes" | "partially" | "no" | "insufficient";
    assessment: string;
    tradeoff: { gain: string; giveUp: string };
    questions: string[];
    sources?: Array<{ uri: string; title?: string; snippet: string }>;
  }>;
  tradeoffs: Array<{
    criterion: string;
    score: "yes" | "partially" | "no" | "insufficient";
    gain: string;
    giveUp: string;
  }>;
  keyQuestions: string[];
  procurementAssessment?: string;
}

interface CitationEntry {
  index: number;
  uri: string;
  title: string;
}

// ---------------------------------------------------------------------------
// Citation helpers
// ---------------------------------------------------------------------------

// Gemini file-search uploads are named "{domain}-{timestamp}.md".
// The grounding chunks may surface this as a URI (with https:// prefix)
// or as a title. This regex captures the domain part.
const GEMINI_FILE_RE = /(?:https?:\/\/)?([a-z0-9](?:[a-z0-9.-]*[a-z0-9])?\.(?:com|org|net|io|co|dev|ai|gov|edu|app|cloud|tech|xyz|me|info|biz|us|uk|de|fr|ca|au|in|jp|br|it|nl|ru|se|no|fi|dk|es|pt|ch|at|be|cz|pl|ie|nz|sg|hk|kr|tw|mx|ar|cl|za|ae|il|sa|qa|my|id|ph|th|vn|ng)(?:\.[a-z]{2})?)-\d{10,}\.md$/i;

/**
 * True if the string is a Gemini file-search reference rather than a
 * real web page URL/title. Handles both bare filenames and full URLs.
 */
function isGeminiRef(value: string): boolean {
  return GEMINI_FILE_RE.test(value);
}

/** Extract the real domain from a Gemini "{domain}-{timestamp}.md" reference. */
function domainFromGeminiRef(value: string): string | null {
  const m = value.match(GEMINI_FILE_RE);
  return m ? m[1] : null;
}

/** Try to pull a real web URL from snippet text (same heuristics as the backend). */
function extractUrlFromSnippet(snippet: string): string | null {
  if (!snippet) return null;
  const patterns = [
    /\*\*Source URL:\*\*\s*(https?:\/\/[^\s\n]+)/i,
    /URL:\s*(https?:\/\/[^\s\n]+)/i,
    /\[(?:[^\]]+)\]\((https?:\/\/[^)\s"]+)/,
    /(https?:\/\/[^\s<>"')\]]+)/,
  ];
  for (const re of patterns) {
    const m = snippet.match(re);
    if (m) {
      const url = m[1];
      if (!isGeminiRef(url)) return url;
    }
  }
  return null;
}

/** Strip query params / hash for a cleaner display URL. */
function cleanUrl(uri: string): string {
  try {
    const url = new URL(uri);
    return url.origin + url.pathname.replace(/\/$/, "");
  } catch {
    return uri.split("?")[0].split("#")[0];
  }
}

/** Derive a readable title from a URL path. */
function titleFromUrl(uri: string): string {
  try {
    const url = new URL(uri);
    const parts = url.pathname.split("/").filter(Boolean);
    if (parts.length === 0) return url.hostname;
    return parts[parts.length - 1]
      .replace(/[-_]/g, " ")
      .replace(/\.[^.]+$/, "")
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  } catch {
    return uri;
  }
}

/**
 * Resolve the best display URI and title for a grounding source.
 * Falls back through: snippet URL → original URI → domain from Gemini ref.
 */
function resolveSource(source: {
  uri: string;
  title?: string;
  snippet: string;
}): { uri: string; title: string } {
  // If the URI is already a real page, use it directly
  if (!isGeminiRef(source.uri)) {
    const clean = cleanUrl(source.uri);
    const title =
      source.title && !isGeminiRef(source.title)
        ? source.title
        : titleFromUrl(source.uri);
    return { uri: clean, title };
  }

  // URI is a Gemini reference — try to find the real URL in the snippet
  const snippetUrl = extractUrlFromSnippet(source.snippet);
  if (snippetUrl) {
    const clean = cleanUrl(snippetUrl);
    const title =
      source.title && !isGeminiRef(source.title)
        ? source.title
        : titleFromUrl(snippetUrl);
    return { uri: clean, title };
  }

  // Last resort: extract domain from the Gemini filename pattern
  const domain = domainFromGeminiRef(source.uri) ?? domainFromGeminiRef(source.title ?? "");
  if (domain) {
    return { uri: `https://${domain}`, title: domain };
  }

  return { uri: source.uri, title: source.title || source.uri };
}

function buildCitationRegistry(
  criteria: PdfReportData["criteria"],
): { registry: Map<string, CitationEntry>; list: CitationEntry[] } {
  const registry = new Map<string, CitationEntry>();
  const list: CitationEntry[] = [];

  for (const criterion of criteria) {
    if (!criterion.sources) continue;
    for (const source of criterion.sources) {
      const resolved = resolveSource(source);
      if (!registry.has(resolved.uri)) {
        const entry: CitationEntry = {
          index: list.length + 1,
          uri: resolved.uri,
          title: resolved.title,
        };
        registry.set(resolved.uri, entry);
        list.push(entry);
      }
    }
  }
  return { registry, list };
}

function getCitationNumbers(
  sources: Array<{ uri: string; title?: string; snippet: string }> | undefined,
  registry: Map<string, CitationEntry>,
): number[] {
  if (!sources) return [];
  return sources
    .map((s) => {
      const resolved = resolveSource(s);
      return registry.get(resolved.uri)?.index;
    })
    .filter((n): n is number => n !== undefined);
}

// ---------------------------------------------------------------------------
// Colors & Score config
// ---------------------------------------------------------------------------

const C = {
  primary: "#1e3a5f",
  accent: "#d4975c",
  lightBg: "#f0ebe4",
  border: "#e8e3dc",
  body: "#3a4a5a",
  muted: "#5a6a7a",
  white: "#ffffff",
  pageBg: "#f8f5f0",
} as const;

const SCORE_CFG = {
  yes: { label: "Met", symbol: "✓", color: "#059669", bg: "#ecfdf5" },
  partially: { label: "Partially Met", symbol: "~", color: "#d97706", bg: "#fffbeb" },
  no: { label: "Not Met", symbol: "✗", color: "#e11d48", bg: "#fff1f2" },
  insufficient: { label: "Insufficient Info", symbol: "?", color: "#475569", bg: "#f8fafc" },
} as const;

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const s = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    fontSize: 10,
    color: C.body,
    paddingTop: 40,
    paddingBottom: 50,
    paddingHorizontal: 40,
    backgroundColor: C.white,
  },
  pageNumber: {
    position: "absolute",
    bottom: 20,
    right: 40,
    fontSize: 8,
    color: C.muted,
  },

  // Header
  headerBar: {
    backgroundColor: C.primary,
    marginHorizontal: -40,
    marginTop: -40,
    paddingHorizontal: 40,
    paddingVertical: 28,
    marginBottom: 24,
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  headerVendor: {
    fontFamily: "Times-Roman",
    fontSize: 24,
    color: C.white,
    marginBottom: 2,
  },
  headerSubtitle: {
    fontSize: 9,
    color: C.accent,
    letterSpacing: 2,
    textTransform: "uppercase",
  },
  headerMeta: {
    fontSize: 9,
    color: "#94a3b8",
    textAlign: "right",
  },
  headerCategory: {
    fontSize: 10,
    color: "#cbd5e1",
    marginTop: 6,
  },

  // Generic section
  sectionTitle: {
    fontFamily: "Times-Bold",
    fontSize: 16,
    color: C.primary,
    marginBottom: 2,
  },
  sectionAccent: {
    width: 40,
    height: 2,
    backgroundColor: C.accent,
    borderRadius: 1,
    marginBottom: 6,
  },
  sectionSubtitle: {
    fontSize: 9,
    color: C.muted,
    marginBottom: 14,
  },

  // Cards
  card: {
    backgroundColor: C.white,
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 6,
    padding: 16,
    marginBottom: 10,
  },

  // Text
  bodyText: {
    fontSize: 10,
    lineHeight: 1.6,
    color: C.body,
  },
  bold: {
    fontFamily: "Helvetica-Bold",
  },
  label: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    letterSpacing: 1,
    textTransform: "uppercase",
    marginBottom: 4,
  },

  // Criterion card
  criterionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  criterionTitle: {
    fontFamily: "Times-Bold",
    fontSize: 14,
    color: C.primary,
  },
  criterionSubtitle: {
    fontSize: 9,
    color: C.muted,
    marginTop: 1,
  },
  scoreBadge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 4,
    borderWidth: 1.5,
  },
  scoreBadgeSymbol: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    marginRight: 4,
  },
  scoreBadgeLabel: {
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
  },

  // Tradeoff box
  tradeoffBox: {
    backgroundColor: "#f8fafc",
    borderWidth: 1,
    borderColor: "#e2e8f0",
    borderRadius: 4,
    padding: 12,
    marginTop: 8,
  },
  tradeoffRow: {
    flexDirection: "row",
    gap: 16,
  },
  tradeoffCol: {
    flex: 1,
  },
  tradeoffDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginTop: 3,
    marginRight: 6,
  },

  // Questions box
  questionsBox: {
    backgroundColor: "#eff6ff",
    borderWidth: 1,
    borderColor: "#bfdbfe",
    borderRadius: 4,
    padding: 12,
    marginTop: 8,
  },

  // Citation superscript
  citationSup: {
    fontSize: 7,
    color: C.accent,
    fontFamily: "Helvetica-Bold",
    verticalAlign: "super",
  },

  // Score overview grid
  overviewGrid: {
    flexDirection: "row",
    justifyContent: "space-around",
    marginBottom: 12,
  },
  overviewItem: {
    alignItems: "center",
    width: 80,
  },
  overviewCount: {
    fontFamily: "Helvetica-Bold",
    fontSize: 22,
    color: C.primary,
  },
  overviewLabel: {
    fontSize: 8,
    color: C.muted,
    marginTop: 2,
  },
  overviewChipRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: 6,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: "#e2e8f0",
  },
  overviewChip: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#f8fafc",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    gap: 4,
  },

  // Tradeoff summary table
  tableHeader: {
    flexDirection: "row",
    backgroundColor: C.lightBg,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#f1f5f9",
  },
  tableColCriterion: { width: "16%" },
  tableColScore: { width: "10%", alignItems: "center" },
  tableColGain: { width: "37%" },
  tableColGiveUp: { width: "37%" },
  tableHeaderText: {
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    color: C.primary,
  },
  tableCellText: {
    fontSize: 9,
    color: C.body,
    lineHeight: 1.5,
  },

  // Key questions
  questionRow: {
    flexDirection: "row",
    gap: 10,
    marginBottom: 8,
  },
  questionNumber: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: C.accent,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  questionNumberText: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: C.white,
  },

  // References
  refRow: {
    flexDirection: "row",
    marginBottom: 4,
    gap: 6,
  },
  refIndex: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: C.accent,
    width: 16,
    textAlign: "right",
  },
  refLink: {
    fontSize: 8,
    color: C.primary,
    textDecoration: "underline",
    flex: 1,
  },

  // Footer
  footerBar: {
    backgroundColor: C.primary,
    marginHorizontal: -40,
    marginBottom: -50,
    paddingHorizontal: 40,
    paddingVertical: 20,
    alignItems: "center",
    marginTop: 20,
  },
  footerAccent: {
    fontSize: 8,
    color: C.accent,
    letterSpacing: 1.5,
    textTransform: "uppercase",
    marginBottom: 4,
  },
  footerText: {
    fontSize: 8,
    color: "#94a3b8",
    textAlign: "center",
    lineHeight: 1.5,
  },

  divider: {
    height: 1,
    backgroundColor: "#e2e8f0",
    marginVertical: 10,
  },

  // Markdown-ish helpers for procurement
  mdH2: {
    fontFamily: "Times-Bold",
    fontSize: 13,
    color: C.primary,
    marginTop: 14,
    marginBottom: 6,
  },
  mdH3: {
    fontFamily: "Helvetica-Bold",
    fontSize: 11,
    color: C.primary,
    marginTop: 10,
    marginBottom: 4,
  },
  mdBullet: {
    flexDirection: "row",
    paddingLeft: 8,
    marginBottom: 3,
  },
  mdBulletDot: {
    fontSize: 10,
    color: C.body,
    marginRight: 6,
    width: 10,
  },
  mdParagraph: {
    fontSize: 10,
    color: C.body,
    lineHeight: 1.6,
    marginBottom: 6,
  },
  mdHr: {
    height: 1,
    backgroundColor: "#e2e8f0",
    marginVertical: 10,
  },
});

// ---------------------------------------------------------------------------
// Small components
// ---------------------------------------------------------------------------

function SectionHeading({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) {
  return (
    <View style={{ marginBottom: subtitle ? 0 : 14 }}>
      <Text style={s.sectionTitle}>{title}</Text>
      <View style={s.sectionAccent} />
      {subtitle && <Text style={s.sectionSubtitle}>{subtitle}</Text>}
    </View>
  );
}

function ScoreBadge({
  score,
}: {
  score: "yes" | "partially" | "no" | "insufficient";
}) {
  const cfg = SCORE_CFG[score];
  return (
    <View
      style={[
        s.scoreBadge,
        { backgroundColor: cfg.bg, borderColor: cfg.color },
      ]}
    >
      <Text style={[s.scoreBadgeSymbol, { color: cfg.color }]}>
        {cfg.symbol}
      </Text>
      <Text style={[s.scoreBadgeLabel, { color: cfg.color }]}>
        {cfg.label}
      </Text>
    </View>
  );
}

function CitationMarkers({ numbers }: { numbers: number[] }) {
  if (numbers.length === 0) return null;
  return (
    <Text style={s.citationSup}>
      {" "}
      [{numbers.join(", ")}]
    </Text>
  );
}

// ---------------------------------------------------------------------------
// Markdown renderer (simple subset for procurement assessment)
// ---------------------------------------------------------------------------

function MarkdownContent({ text }: { text: string }) {
  const lines = text.split("\n");
  const elements: React.ReactNode[] = [];
  let paragraphBuffer: string[] = [];
  let key = 0;

  function flushParagraph() {
    if (paragraphBuffer.length > 0) {
      const joined = paragraphBuffer.join(" ").trim();
      if (joined) {
        elements.push(
          <Text key={key++} style={s.mdParagraph}>
            {renderInlineMarkdown(joined)}
          </Text>,
        );
      }
      paragraphBuffer = [];
    }
  }

  for (const line of lines) {
    const trimmed = line.trim();

    if (trimmed === "") {
      flushParagraph();
      continue;
    }
    if (trimmed === "---") {
      flushParagraph();
      elements.push(<View key={key++} style={s.mdHr} />);
      continue;
    }
    if (trimmed.startsWith("## ")) {
      flushParagraph();
      elements.push(
        <Text key={key++} style={s.mdH2}>
          {trimmed.slice(3)}
        </Text>,
      );
      continue;
    }
    if (trimmed.startsWith("### ")) {
      flushParagraph();
      elements.push(
        <Text key={key++} style={s.mdH3}>
          {trimmed.slice(4)}
        </Text>,
      );
      continue;
    }
    if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
      flushParagraph();
      elements.push(
        <View key={key++} style={s.mdBullet}>
          <Text style={s.mdBulletDot}>•</Text>
          <Text style={[s.mdParagraph, { flex: 1, marginBottom: 0 }]}>
            {renderInlineMarkdown(trimmed.slice(2))}
          </Text>
        </View>,
      );
      continue;
    }
    paragraphBuffer.push(trimmed);
  }
  flushParagraph();
  return <>{elements}</>;
}

function renderInlineMarkdown(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const regex = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <Text key={`b${i++}`} style={s.bold}>
        {match[1]}
      </Text>,
    );
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
}

// ---------------------------------------------------------------------------
// Section components
// ---------------------------------------------------------------------------

function Header({ data }: { data: PdfReportData }) {
  return (
    <View style={s.headerBar} fixed>
      <View style={s.headerRow}>
        <View style={{ flex: 1 }}>
          <Text style={s.headerVendor}>{data.vendor}</Text>
          <Text style={s.headerSubtitle}>Vendor Evaluation Report</Text>
        </View>
        <View>
          <Text style={s.headerMeta}>Version {data.version}</Text>
          <Text style={s.headerMeta}>{data.evaluationDate}</Text>
        </View>
      </View>
      <Text style={s.headerCategory}>{data.category}</Text>
    </View>
  );
}

function ExecutiveSummarySection({
  data,
}: {
  data: PdfReportData["executiveSummary"];
}) {
  const borderColors = {
    positive: "#059669",
    neutral: "#d97706",
    negative: "#e11d48",
  };
  const bgColors = {
    positive: "#ecfdf5",
    neutral: "#fffbeb",
    negative: "#fff1f2",
  };
  return (
    <View style={{ marginBottom: 16 }}>
      <SectionHeading title="Executive Summary" />
      <View
        style={[
          s.card,
          {
            borderLeftWidth: 4,
            borderLeftColor: borderColors[data.sentiment],
            backgroundColor: bgColors[data.sentiment],
          },
        ]}
      >
        <Text style={[s.label, { color: C.muted }]}>KEY TAKEAWAY</Text>
        <Text
          style={[
            s.bodyText,
            { fontFamily: "Helvetica-Bold", fontSize: 11, marginBottom: 10 },
          ]}
        >
          {data.keyTakeaway}
        </Text>
        <View style={s.divider} />
        {data.paragraphs.map((p, i) => (
          <Text key={i} style={[s.bodyText, { marginBottom: 6 }]}>
            {p}
          </Text>
        ))}
      </View>
    </View>
  );
}

function ScoreOverviewSection({
  criteria,
}: {
  criteria: PdfReportData["criteria"];
}) {
  const counts = {
    yes: criteria.filter((c) => c.score === "yes").length,
    partially: criteria.filter((c) => c.score === "partially").length,
    no: criteria.filter((c) => c.score === "no").length,
    insufficient: criteria.filter((c) => c.score === "insufficient").length,
  };

  return (
    <View style={[s.card, { marginBottom: 16 }]} wrap={false}>
      <Text
        style={[
          s.sectionTitle,
          { fontSize: 13, marginBottom: 12, textAlign: "center" },
        ]}
      >
        Evaluation Overview
      </Text>
      <View style={s.overviewGrid}>
        {(
          Object.entries(counts) as [keyof typeof SCORE_CFG, number][]
        ).map(([key, count]) => (
          <View key={key} style={s.overviewItem}>
            <Text style={[s.overviewCount, { color: SCORE_CFG[key].color }]}>
              {count}
            </Text>
            <Text style={s.overviewLabel}>{SCORE_CFG[key].label}</Text>
          </View>
        ))}
      </View>
      <View style={s.overviewChipRow}>
        {criteria.map((c, i) => (
          <View key={i} style={s.overviewChip}>
            <Text
              style={{
                fontSize: 9,
                color: SCORE_CFG[c.score].color,
                fontFamily: "Helvetica-Bold",
              }}
            >
              {SCORE_CFG[c.score].symbol}
            </Text>
            <Text style={{ fontSize: 8, color: C.body }}>{c.title}</Text>
          </View>
        ))}
      </View>
    </View>
  );
}

function CriterionSection({
  criterion,
  citationNumbers,
}: {
  criterion: PdfReportData["criteria"][number];
  citationNumbers: number[];
}) {
  const hasTradeoffs = criterion.tradeoff.gain || criterion.tradeoff.giveUp;
  const hasQuestions = criterion.questions && criterion.questions.length > 0;

  return (
    <View style={[s.card, { marginBottom: 10 }]}>
      {/* Header row - keep together */}
      <View style={s.criterionHeader} wrap={false}>
        <View style={{ flex: 1 }}>
          <Text style={s.criterionTitle}>{criterion.title}</Text>
          <Text style={s.criterionSubtitle}>{criterion.subtitle}</Text>
        </View>
        <ScoreBadge score={criterion.score} />
      </View>

      <View style={s.divider} />

      {/* Assessment */}
      <View style={{ marginBottom: 6 }}>
        <Text style={[s.label, { color: C.muted }]}>ASSESSMENT</Text>
        <Text style={s.bodyText}>
          {criterion.assessment}
          <CitationMarkers numbers={citationNumbers} />
        </Text>
      </View>

      {/* Trade-offs */}
      {hasTradeoffs && (
        <View style={s.tradeoffBox} wrap={false}>
          <Text style={[s.label, { color: C.muted, marginBottom: 6 }]}>
            TRADE-OFFS
          </Text>
          <View style={s.tradeoffRow}>
            {criterion.tradeoff.gain ? (
              <View style={[s.tradeoffCol, { flexDirection: "row" }]}>
                <View
                  style={[
                    s.tradeoffDot,
                    { backgroundColor: "#059669" },
                  ]}
                />
                <View style={{ flex: 1 }}>
                  <Text
                    style={[
                      s.label,
                      { color: "#059669", fontSize: 7, marginBottom: 2 },
                    ]}
                  >
                    WHAT YOU GAIN
                  </Text>
                  <Text style={{ fontSize: 9, color: C.body, lineHeight: 1.5 }}>
                    {criterion.tradeoff.gain}
                  </Text>
                </View>
              </View>
            ) : null}
            {criterion.tradeoff.giveUp ? (
              <View style={[s.tradeoffCol, { flexDirection: "row" }]}>
                <View
                  style={[
                    s.tradeoffDot,
                    { backgroundColor: "#e11d48" },
                  ]}
                />
                <View style={{ flex: 1 }}>
                  <Text
                    style={[
                      s.label,
                      { color: "#e11d48", fontSize: 7, marginBottom: 2 },
                    ]}
                  >
                    WHAT YOU GIVE UP
                  </Text>
                  <Text style={{ fontSize: 9, color: C.body, lineHeight: 1.5 }}>
                    {criterion.tradeoff.giveUp}
                  </Text>
                </View>
              </View>
            ) : null}
          </View>
        </View>
      )}

      {/* Questions */}
      {hasQuestions && (
        <View style={s.questionsBox} wrap={false}>
          <Text style={[s.label, { color: "#1e40af", marginBottom: 6 }]}>
            QUESTIONS FOR VENDOR
          </Text>
          {criterion.questions.map((q, i) => (
            <View key={i} style={{ flexDirection: "row", marginBottom: 3 }}>
              <Text
                style={{
                  fontSize: 9,
                  color: "#2563eb",
                  fontFamily: "Helvetica-Bold",
                  width: 14,
                }}
              >
                {i + 1}.
              </Text>
              <Text style={{ fontSize: 9, color: C.body, flex: 1, lineHeight: 1.5 }}>
                {q}
              </Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );
}

function TradeoffTableSection({
  tradeoffs,
}: {
  tradeoffs: PdfReportData["tradeoffs"];
}) {
  return (
    <View style={{ marginBottom: 16 }}>
      <SectionHeading title="Trade-off Summary" />
      <View style={[s.card, { padding: 0, overflow: "hidden" }]}>
        {/* Header row */}
        <View style={s.tableHeader} wrap={false}>
          <View style={s.tableColCriterion}>
            <Text style={s.tableHeaderText}>Criterion</Text>
          </View>
          <View style={s.tableColScore}>
            <Text style={s.tableHeaderText}>Score</Text>
          </View>
          <View style={s.tableColGain}>
            <Text style={s.tableHeaderText}>What You Gain</Text>
          </View>
          <View style={s.tableColGiveUp}>
            <Text style={s.tableHeaderText}>What You Give Up</Text>
          </View>
        </View>
        {/* Data rows */}
        {tradeoffs.map((row, i) => {
          const cfg = SCORE_CFG[row.score];
          return (
            <View key={i} style={s.tableRow} wrap={false}>
              <View style={s.tableColCriterion}>
                <Text
                  style={[
                    s.tableCellText,
                    { fontFamily: "Helvetica-Bold", color: C.primary },
                  ]}
                >
                  {row.criterion}
                </Text>
              </View>
              <View style={s.tableColScore}>
                <Text
                  style={{
                    fontSize: 11,
                    fontFamily: "Helvetica-Bold",
                    color: cfg.color,
                  }}
                >
                  {cfg.symbol}
                </Text>
              </View>
              <View style={s.tableColGain}>
                <Text style={s.tableCellText}>
                  {row.gain || "—"}
                </Text>
              </View>
              <View style={s.tableColGiveUp}>
                <Text style={s.tableCellText}>
                  {row.giveUp || "—"}
                </Text>
              </View>
            </View>
          );
        })}
      </View>
    </View>
  );
}

function ProcurementSection({ text }: { text: string }) {
  return (
    <View style={{ marginBottom: 16 }}>
      <SectionHeading
        title="Procurement Assessment"
        subtitle="Enterprise procurement fitness evaluation covering key criteria for large-firm best practices"
      />
      <View style={s.card}>
        <MarkdownContent text={text} />
      </View>
    </View>
  );
}

function KeyQuestionsSection({
  questions,
}: {
  questions: string[];
}) {
  return (
    <View style={{ marginBottom: 16 }}>
      <SectionHeading
        title="Key Questions for Your Decision"
        subtitle="Consider these strategic questions based on your organizational context and priorities."
      />
      {questions.map((q, i) => (
        <View key={i} style={s.questionRow} wrap={false}>
          <View style={s.questionNumber}>
            <Text style={s.questionNumberText}>{i + 1}</Text>
          </View>
          <Text
            style={[s.bodyText, { flex: 1, paddingTop: 2 }]}
          >
            {q}
          </Text>
        </View>
      ))}
    </View>
  );
}

function ReferencesSection({ citations }: { citations: CitationEntry[] }) {
  if (citations.length === 0) return null;
  return (
    <View style={{ marginTop: 8 }}>
      <SectionHeading title="References" />
      {citations.map((c) => (
        <View key={c.index} style={s.refRow} wrap={false}>
          <Text style={s.refIndex}>[{c.index}]</Text>
          <Link src={c.uri} style={s.refLink}>
            {c.uri}
          </Link>
        </View>
      ))}
    </View>
  );
}

function FooterBlock({ version }: { version: string }) {
  return (
    <View style={s.footerBar}>
      <Text style={s.footerAccent}>
        Confidential — For Internal Use Only
      </Text>
      <Text style={s.footerText}>
        This evaluation framework assesses vendors on immediate utility and
        long-term organizational AI capacity building.
      </Text>
      <Text style={[s.footerText, { marginTop: 4, fontSize: 7 }]}>
        Framework designed for Fortune 500 executive decision-making • Version{" "}
        {version}
      </Text>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Document
// ---------------------------------------------------------------------------

export function ReportPdfDocument({ data }: { data: PdfReportData }) {
  const { registry, list: citations } = buildCitationRegistry(data.criteria);

  return (
    <Document
      title={`${data.vendor} — Vendor Evaluation Report`}
      author="FeedForward AI"
    >
      <Page size="A4" style={s.page}>
        <Header data={data} />

        <ExecutiveSummarySection data={data.executiveSummary} />

        <ScoreOverviewSection criteria={data.criteria} />

        {/* Detailed Evaluation */}
        <View style={{ marginBottom: 16 }}>
          <SectionHeading
            title="Detailed Evaluation"
            subtitle="Assessment across six critical criteria for AI vendor evaluation"
          />
          {data.criteria.map((criterion) => (
            <CriterionSection
              key={criterion.criterionId}
              criterion={criterion}
              citationNumbers={getCitationNumbers(
                criterion.sources,
                registry,
              )}
            />
          ))}
        </View>

        <TradeoffTableSection tradeoffs={data.tradeoffs} />

        {data.procurementAssessment && (
          <ProcurementSection text={data.procurementAssessment} />
        )}

        <KeyQuestionsSection questions={data.keyQuestions} />

        <ReferencesSection citations={citations} />

        <FooterBlock version={data.version} />

        <Text
          style={s.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} / ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  );
}
